// Email review queue: the rows Owlbert marks and a human agrees to.
// Thin helpers over public.email_items.
//
// The generated Database type in supabase.ts does not know this table yet
// (that typing lands with the migration), so this module talks to the client
// untyped and narrows results to the row types declared here — same shape as
// lib/contentPieces.ts.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import { uniqueChannelName } from './realtime';

export type EmailCategory =
  | 'cold_outreach'
  | 'marketing'
  | 'client'
  | 'vendor'
  | 'internal'
  | 'notification'
  | 'personal'
  | 'other';

export type EmailProposedAction =
  | 'none'
  | 'mark_read'
  | 'trash'
  | 'escalate_comms'
  | 'reply';

export type EmailItemStatus =
  | 'pending'
  | 'agreed'
  | 'disagreed'
  | 'executed'
  | 'failed'
  | 'send_requested'
  | 'sent'
  | 'undo_requested';

export type EmailItem = {
  id: string;
  account: string;
  gmail_message_id: string;
  gmail_thread_id: string;
  from_name: string | null;
  from_email: string;
  subject: string | null;
  snippet: string | null;
  received_at: string;
  is_unread: boolean;
  category: EmailCategory | null;
  proposed_action: EmailProposedAction | null;
  reasoning: string | null;
  recommended_reply: string | null;
  client_id: string | null;
  status: EmailItemStatus;
  decided_by: string | null;
  decided_at: string | null;
  /**
   * The auto-agree breaker agreed to this row, not a person — `decided_by` is
   * NULL on these, and a human decision always carries one. The two must stay
   * readable apart: rule learning trains on human corrections, so counting a
   * machine decision as a human one would teach the rules from their own
   * output.
   */
  auto_agreed: boolean;
  disagree_reason: string | null;
  disagreed_with_action: EmailProposedAction | null;
  executed_at: string | null;
  sent_at: string | null;
  execution_error: string | null;
  created_at: string;
  updated_at: string;
  // Table needs an index signature to stay compatible with ui/Table
  [key: string]: unknown;
};

export const CATEGORY_LABELS: Record<EmailCategory, string> = {
  cold_outreach: 'Cold outreach',
  marketing: 'Marketing',
  client: 'Client',
  vendor: 'Vendor',
  internal: 'Internal',
  notification: 'Notification',
  personal: 'Personal',
  other: 'Other',
};

export const ACTION_LABELS: Record<EmailProposedAction, string> = {
  none: 'Leave it',
  mark_read: 'Mark read',
  trash: 'Trash',
  escalate_comms: 'To comms',
  reply: 'Reply',
};

export const STATUS_LABELS: Record<EmailItemStatus, string> = {
  pending: 'Pending',
  agreed: 'Agreed',
  disagreed: 'Disagreed',
  executed: 'Executed',
  failed: 'Failed',
  send_requested: 'Sending',
  sent: 'Sent',
  undo_requested: 'Undoing',
};

type BadgeTone = 'up' | 'warn' | 'down' | 'info' | 'idle' | 'accent' | 'neutral';

// Client work reads as the important lane; the junk lanes stay quiet.
export function categoryTone(category: EmailCategory | null): BadgeTone {
  switch (category) {
    case 'client':
      return 'up';
    case 'vendor':
      return 'info';
    case 'internal':
      return 'accent';
    case 'personal':
      return 'warn';
    case 'cold_outreach':
    case 'marketing':
    case 'notification':
      return 'idle';
    default:
      return 'neutral';
  }
}

// Destructive proposals read hot so a reviewer never agrees to a trash on autopilot.
export function actionTone(action: EmailProposedAction | null): BadgeTone {
  switch (action) {
    case 'trash':
      return 'down';
    case 'reply':
      return 'accent';
    case 'escalate_comms':
      return 'info';
    case 'mark_read':
      return 'idle';
    case 'none':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function statusTone(status: EmailItemStatus): BadgeTone {
  switch (status) {
    case 'pending':
      return 'warn';
    case 'agreed':
      return 'accent';
    case 'disagreed':
      return 'down';
    case 'executed':
      return 'up';
    case 'failed':
      return 'down';
    case 'send_requested':
      return 'warn';
    case 'sent':
      return 'up';
    case 'undo_requested':
      return 'warn';
    default:
      return 'neutral';
  }
}

/** Deep link to the thread in the right mailbox. Gmail accepts the account
 *  address in the /u/ segment and resolves it against the signed-in profiles.
 *
 *  Anchored on #all/ rather than #inbox/ deliberately: #inbox/<id> only
 *  resolves while the thread still carries the INBOX label, so an archived
 *  message — or one we ourselves marked read and filed — would open to an
 *  empty view. #all/ searches every message and always lands. */
export function gmailThreadUrl(item: EmailItem): string {
  // /u/0/ means "the first Google account signed into this browser", which for
  // each of us is our own. Kyle confirmed this form resolves; the two variants
  // that 404'd both put the address in the path — /u/kyle@bullfinch.io/ and its
  // percent-encoded cousin. Gmail's /u/ segment takes an index, not an address.
  //
  // The hex thread id from the Gmail API is fine here: #all/<id> resolves it.
  // Gmail's web client shows a different id in the address bar, but that is a
  // display form, not a requirement.
  //
  // `account` is deliberately unused. It stays on the row because ingest and
  // the executor both need it; the URL cannot express it.
  const thread = item.gmail_thread_id.replace(/[^A-Za-z0-9_-]/g, '');
  return `https://mail.google.com/mail/u/0/#all/${thread}`;
}

/** Compact age: minutes to 60, hours to 24, days to 7, then weeks, months, years.
 *  Mirrors lib/comms.ts so the two inboxes read the same. */
export function age(iso: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
}

export function excerpt(text: string | null, max = 140): string {
  if (!text) return '';
  const flat = text.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;
const db = supabase as unknown as LooseClient;

function emailItems() {
  return db.from('email_items');
}

/* ---------- queries ---------- */

/**
 * 'unread' is not a status — it is the is_unread flag, which mirrors Gmail's
 * UNREAD label as of the last sync. Offering it beside the statuses is a lie
 * about the schema but the truth about the question being asked: "what have I
 * not read yet?"
 */
export type StatusOrUnread = EmailItemStatus | 'all' | 'unread';

export type ListEmailItemsOptions = {
  status?: StatusOrUnread;
  account?: string | null;
  category?: EmailCategory | null;
  limit?: number;
};

export async function listEmailItems(
  options: ListEmailItemsOptions = {}
): Promise<EmailItem[]> {
  const { status = 'pending', account, category, limit = 500 } = options;
  let query = emailItems()
    .select('*')
    .order('received_at', { ascending: false })
    .limit(limit);
  if (status === 'unread') {
    // Cuts across every status: a row can be executed and still unread if the
    // action was 'none'.
    query = query.eq('is_unread', true);
  } else if (status !== 'all') {
    query = query.eq('status', status);
  }
  if (account) query = query.eq('account', account);
  if (category) query = query.eq('category', category);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as EmailItem[];
}

/** Mailboxes present in the table, for the account filter. Distinct is not a
 *  PostgREST primitive, so pull the column and dedupe here — the row count is
 *  small and the alternative is a view this PR does not need. */
export async function listAccounts(): Promise<string[]> {
  const { data, error } = await emailItems().select('account').limit(2000);
  if (error) throw error;
  const seen = new Set<string>();
  ((data ?? []) as Array<{ account: string }>).forEach((r) => {
    if (r.account) seen.add(r.account);
  });
  return [...seen].sort();
}

export async function countPending(): Promise<number> {
  const { count, error } = await emailItems()
    .select('*', { count: 'exact', head: true })
    .eq('status', 'pending');
  if (error) throw error;
  return count ?? 0;
}

/* ---------- review actions ---------- */

/**
 * Agree to the AI's proposal.
 *
 * TODO(PR 2 — execution): this ONLY records the human's approval. It does not
 * touch Gmail (no mark-read, no trash, no reply) and does not write to
 * comms_items for 'escalate_comms'. A follow-up PR picks up status='agreed'
 * rows, performs the action, and stamps executed_at / execution_error (or
 * status='failed'). Nothing here may be treated as "the action ran".
 */
export async function agreeToEmailItem(id: string, userId: string | null): Promise<EmailItem> {
  const { data, error } = await emailItems()
    .update({
      status: 'agreed',
      decided_by: userId,
      decided_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as EmailItem;
}

/**
 * Reject the proposal, and say why.
 *
 * The reason is the point. Owlbert reads these back per mailbox and turns them
 * into explicit classification rules, so a disagreement is a correction to the
 * agent's judgement rather than a row disappearing. The database enforces a
 * non-trivial reason; this throws before the round trip so the UI can say so.
 *
 * `proposedAction` is stored alongside, because "not junk" is only a usable
 * rule if we know which proposal it was aimed at.
 */
export async function disagreeWithEmailItem(
  id: string,
  reason: string,
  proposedAction: EmailProposedAction | null,
  userId: string | null
): Promise<EmailItem> {
  const trimmed = reason.trim();
  if (trimmed.length < 3) throw new Error('A disagreement needs a reason.');
  const { data, error } = await emailItems()
    .update({
      status: 'disagreed',
      disagree_reason: trimmed,
      disagreed_with_action: proposedAction,
      decided_by: userId,
      decided_at: new Date().toISOString(),
    })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as EmailItem;
}

/* ---------- realtime ---------- */

/** Fires on any change to the email queue (new scan results, someone else
 *  agreeing on another screen). */
export function subscribeEmailItems(onChange: () => void): () => void {
  const channel = supabase
    .channel(uniqueChannelName('email_items_channel'))
    .on('postgres_changes', { event: '*', schema: 'public', table: 'email_items' }, onChange)
    .subscribe();
  return () => {
    supabase.removeChannel(channel);
  };
}

/* ---------- pipeline freshness ---------- */

export type PipelineRun = {
  at: string;
  rows?: number;
  mailboxes?: number;
  considered?: number;
  marked?: number;
  unmarked?: number;
};

/**
 * When sync and analysis last ran. Written by scripts/email-{ingest,classify}
 * into the shared settings table, so the screen can say how fresh it is rather
 * than looking equally confident whether the pipeline ran a minute or a day ago.
 */
export async function getPipelineRuns(): Promise<{
  sync: PipelineRun | null;
  analysis: PipelineRun | null;
}> {
  const { data, error } = await db
    .from('settings')
    .select('key, value')
    .in('key', ['email_sync_last_run', 'email_analysis_last_run']);
  if (error) throw error;
  const byKey = Object.fromEntries(
    ((data ?? []) as Array<{ key: string; value: PipelineRun }>).map(r => [r.key, r.value])
  );
  return {
    sync: byKey.email_sync_last_run ?? null,
    analysis: byKey.email_analysis_last_run ?? null,
  };
}

/** Stale once a 5-minute pipeline has missed roughly three cycles. */
export function isStale(run: PipelineRun | null, minutes = 15): boolean {
  if (!run?.at) return true;
  return Date.now() - new Date(run.at).getTime() > minutes * 60_000;
}

/**
 * Queue a reply to actually be sent.
 *
 * This is the ONLY writer of 'send_requested' anywhere in the system, and it
 * is reachable only from a human clicking Send in the reply drawer. Agreeing
 * to a reply drafts it; sending is a second, separate decision.
 *
 * The current text is saved with it, because the drawer allows edits after the
 * draft was written and the executor rebuilds the message from this field.
 */
export async function requestSend(id: string, replyText: string): Promise<EmailItem> {
  const trimmed = replyText.trim();
  if (!trimmed) throw new Error('Nothing to send.');
  const { data, error } = await emailItems()
    .update({ status: 'send_requested', recommended_reply: trimmed })
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as EmailItem;
}

/** Save an edited draft without sending it. */
export async function saveReplyText(id: string, replyText: string): Promise<void> {
  const { error } = await emailItems()
    .update({ recommended_reply: replyText })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Take a decision back.
 *
 * Two speeds, because the cases genuinely differ:
 *   agreed / disagreed  nothing happened in Gmail yet, so this is just a
 *                       status change. Instant.
 *   executed            Gmail was changed, so the executor has to reverse it.
 *                       Parks in 'undo_requested' until the next run.
 *
 * A sent reply is never undoable — see canUndo.
 */
export async function undoDecision(item: EmailItem): Promise<void> {
  if (!canUndo(item)) throw new Error('That cannot be undone.');

  const patch =
    item.status === 'executed'
      ? {
          status: 'undo_requested' as const,
          // Captured now: a later analyze pass can rewrite proposed_action, and
          // the reversal must not depend on reading it back.
          undo_of_action: item.proposed_action,
        }
      : {
          status: 'pending' as const,
          decided_at: null,
          decided_by: null,
          disagree_reason: null,
          disagreed_with_action: null,
        };

  const { error } = await emailItems().update(patch).eq('id', item.id);
  if (error) throw error;
}

/** A sent email cannot be unsent, so it is never offered. */
export function canUndo(item: EmailItem): boolean {
  if (item.status === 'sent' || item.status === 'send_requested') return false;
  if (item.status === 'undo_requested') return false;
  if (item.status === 'executed' && item.proposed_action === 'reply') return false;
  return ['agreed', 'disagreed', 'executed', 'failed'].includes(item.status);
}
